import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {onError} from '@Panels/user-menu-panel/user-menu-panel-helper';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {AlbumData, FacebookResponse} from '@Panels/user-media-panel/user-media-panel.types';
import {COOKIE_FB_SESSION_KEY} from '@Panels/user-media-panel/user-media-panel.types';
import {readCookie} from '@Utils/cookie.util';
import styles from './facebook-albums.module.scss';

interface FacebookAlbumsProps {
  onAlbumSelected(album: AlbumData): void;
}

export function FacebookAlbums({...props}: FacebookAlbumsProps): ReactElement {
  const [albums, setAlbums] = useState<Array<ReactElement>>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    void getUserAlbums();
  }, []);

  const getUserAlbums = async (): Promise<void> => {
    try {
      const response = (await window.PMW.readLocal('user/getFacebookAlbumsJSON', {
        fbSessionKey: readCookie(COOKIE_FB_SESSION_KEY),
      })) as FacebookResponse;
      if (response.albums) {
        showUserAlbums(response.albums);
      }
    } catch (e: any) {
      onError();
    }
    setIsLoading(false);
  };

  const showUserAlbums = (albumsData: Array<AlbumData>): void => {
    if (albumsData.length > 0) {
      const allAlbums = [];

      for (const albumData of albumsData) {
        allAlbums.push(getAlbumHTML(albumData));
      }
      setAlbums(allAlbums);
    }
  };

  const selectAlbum = (album: AlbumData): void => {
    props.onAlbumSelected(album);
  };

  const getAlbumHTML = (album: AlbumData): ReactElement => {
    return (
      <ClickableDiv
        key={album.aid}
        className={styles.album}
        onClick={(): void => {
          selectAlbum(album);
        }}
        title={album.name}
        data-aid={album.aid}
        data-aname={album.name}
      >
        <div className="flex-center radius-4">
          <img className={`radius-4 ${styles.albumImage}`} src={album.cover} alt={album.name} />
        </div>
        <div className="flex-column-justify-center spacing-p-t-2">
          <Text val={album.name} size={TextSize.XSMALL} bold />
          <Text className="content-sub-text" size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_facebook_photos_count', {count: album.size})} />
        </div>
      </ClickableDiv>
    );
  };

  if (isLoading) {
    return (
      <div className="_full-width _full-height flex-center flex-v-row">
        <div>
          <CircularProgressLoader size={LOADER_SIZE.LARGE} />
        </div>
        <Text val={window.i18next.t('pmwjs_loading_fb_albums')} className="content-body" />
      </div>
    );
  }

  if (!albums?.length) {
    return (
      <div className="_full-width _full-height flex-center">
        <Text val={window.i18next.t('pmwjs_no_fb_album')} className="content-body" />
      </div>
    );
  }

  return (
    <div className={`flexbox spacing-p-l-8 spacing-p-r-2 spacing-p-t-10 _full-height ${styles.container}`}>
      <div className={`flexbox _fit-height ${styles.albums}`}>{albums}</div>
    </div>
  );
}
